import type {
  CreatePartnerBranchRequest,
  CreatePartnerRequest,
  PartnerBranchResponse,
  PartnerResponse,
  PartnerStatement,
  PartnerWriteResponse,
  UpdatePartnerBranchRequest,
  UpdatePartnerRequest,
} from '@palaccount/contracts';
import {
  DEFAULT_PAGE_SIZE,
  MAX_PICKER_IDS,
  PICKER_PAGE_SIZE,
  pickerIds,
  type PageSize,
} from '@palaccount/contracts';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/api';
import { throwApiError } from '@/lib/server-messages';

/**
 * The MD-01 data layer: thin TanStack Query wrappers over the typed contract
 * client. Every non-2xx becomes an ApiError carrying the server's fixed
 * sentence — components localize it via localizeServerMessage and, on a 403,
 * record the route's permission (see lib/permissions.ts).
 */

export const PARTNERS_KEY = ['partners'] as const;

export interface PartnerListFilters {
  q?: string | undefined;
  type?: 'customer' | 'supplier' | undefined;
  /** MD-01 field 4's "used for reporting" — slice to one group. */
  group?: string | undefined;
  includeInactive?: boolean | undefined;
}

function partnerQuery(filters: PartnerListFilters) {
  return {
    ...(filters.q ? { q: filters.q } : {}),
    ...(filters.type ? { type: filters.type } : {}),
    ...(filters.group ? { group: filters.group } : {}),
    ...(filters.includeInactive ? { includeInactive: 'true' as const } : {}),
  };
}

/**
 * MD-01's list SCREEN — one page at a time (NFR-42), with `keepPreviousData`
 * so pressing Next updates the figures without the table blinking back to a
 * skeleton.
 */
export function usePagedPartners(filters: PartnerListFilters, page: number, pageSize: number) {
  return useQuery({
    queryKey: [...PARTNERS_KEY, 'page', filters, page, pageSize],
    queryFn: async () => {
      const response = await api.partners.list({
        query: { ...partnerQuery(filters), page, pageSize },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    placeholderData: keepPreviousData,
  });
}

/**
 * THE PARTNER PICKER'S two reads (GEN-50) — the customer/supplier field of
 * every document editor, which PAGINATION.md §5a named as the first route
 * blocked on a typeahead.
 */
export function usePartnerSearch(q: string, filters: PartnerListFilters = {}) {
  return useQuery({
    queryKey: [...PARTNERS_KEY, 'search', q, filters],
    queryFn: async (): Promise<PartnerResponse[]> => {
      const response = await api.partners.list({
        query: { ...partnerQuery({ ...filters, q: q || undefined }), pageSize: PICKER_PAGE_SIZE },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.partners;
    },
    placeholderData: keepPreviousData,
  });
}

/**
 * The partners a form already holds. Not narrowed by type or by the active
 * flag: a document written last year still names its customer after that
 * customer was switched off (BR-MD-01-07 is about what a picker OFFERS).
 */
export function usePartnersHeld(ids: readonly string[]) {
  const key = [...new Set(ids)].sort();
  return useQuery({
    queryKey: [...PARTNERS_KEY, 'held', key],
    enabled: key.length > 0,
    queryFn: async (): Promise<PartnerResponse[]> => {
      const response = await api.partners.list({
        query: { ids: pickerIds(key), pageSize: MAX_PICKER_IDS },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.partners;
    },
  });
}

export function usePartner(partnerId: string | undefined) {
  return useQuery({
    queryKey: [...PARTNERS_KEY, 'one', partnerId],
    enabled: Boolean(partnerId),
    queryFn: async (): Promise<PartnerResponse> => {
      const response = await api.partners.get({ params: { partnerId: partnerId! } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

/**
 * CRM-04's statement — POSTED documents only (ruling R9).
 *
 * Every control on the screen is a QUERY PARAMETER, not a browser filter,
 * and BR-CRM-04-13 is why: the five summary cards and the five aging bands
 * are properties of the whole ledger, and the ledger is paged. A search or a
 * period applied in the browser would narrow the rows the page happens to
 * hold while leaving the figures beside them describing the account — the
 * two halves would then disagree, and neither would be wrong in a way a
 * reader could see. Asking the server keeps the response internally
 * consistent by construction.
 *
 * `keepPreviousData` so pressing Next, or typing in the search box, updates
 * the table without the whole statement blinking back to a skeleton.
 */
export interface StatementFilters {
  side?: 'customer' | 'supplier' | undefined;
  from?: string | undefined;
  to?: string | undefined;
  q?: string | undefined;
  /** Which currency's statement; absent = the partner's own. */
  currency?: string | undefined;
}

export function usePartnerStatement(
  partnerId: string | undefined,
  filters: StatementFilters = {},
  page = 1,
  /**
   * A block, for every screen read. The unbounded opt-out is taken by exactly
   * one caller — the printable ledger in `partner-statement-card.tsx`, which
   * is where the word itself is written and where `UNPAGED_CALLERS` points.
   */
  pageSize: PageSize = DEFAULT_PAGE_SIZE,
) {
  return useQuery({
    queryKey: [...PARTNERS_KEY, 'statement', partnerId, filters, page, pageSize],
    enabled: Boolean(partnerId),
    queryFn: async (): Promise<PartnerStatement> => {
      const response = await api.partners.statement({
        params: { partnerId: partnerId! },
        query: {
          ...(filters.side ? { side: filters.side } : {}),
          ...(filters.from ? { from: filters.from } : {}),
          ...(filters.to ? { to: filters.to } : {}),
          ...(filters.q ? { q: filters.q } : {}),
          ...(filters.currency ? { currency: filters.currency } : {}),
          page,
          pageSize,
        },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    placeholderData: keepPreviousData,
  });
}

/**
 * CRM-04 §5 "Send Statement" — the one outward-facing action on this screen.
 *
 * The server QUEUES the message and answers with the address, so this
 * mutation resolves as soon as the row is written rather than when a provider
 * replies. That is what lets §5's confirmation be honest at 2.6 seconds: it
 * reports "we have taken this on", which is true, instead of "it has arrived",
 * which nothing on a screen can ever know.
 */
export function useSendStatement(partnerId: string) {
  return useMutation({
    /** `currency`: the statement on screen; absent = the partner's own. */
    mutationFn: async (currency?: string): Promise<{ emailedTo: string }> => {
      const response = await api.partners.sendStatement({
        params: { partnerId },
        body: currency ? { currency } : {},
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

export function useCreatePartner() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreatePartnerRequest): Promise<PartnerWriteResponse> => {
      const response = await api.partners.create({ body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PARTNERS_KEY }),
  });
}

export function useUpdatePartner(partnerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdatePartnerRequest): Promise<PartnerWriteResponse> => {
      const response = await api.partners.update({ params: { partnerId }, body });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PARTNERS_KEY }),
  });
}

/**
 * INV-F3 — the partner's BRANCHES. One key per partner under PARTNERS_KEY, so
 * a partner-wide invalidation (a rename, a switch-off) refreshes them too.
 * `includeInactive` is the partner form's switch; the sales header's picker
 * asks without it and OFFERS the active only (a saved document still DISPLAYS
 * whatever it names — the BR-MD-01-07 offer/display line, one level down).
 */
export function usePartnerBranches(partnerId: string | undefined, includeInactive = false) {
  return useQuery({
    queryKey: [...PARTNERS_KEY, 'branches', partnerId, includeInactive],
    enabled: Boolean(partnerId),
    queryFn: async (): Promise<PartnerBranchResponse[]> => {
      const response = await api.partners.listBranches({
        params: { partnerId: partnerId! },
        query: includeInactive ? { includeInactive: 'true' } : {},
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.branches;
    },
  });
}

export function usePartnerBranchMutations(partnerId: string) {
  const queryClient = useQueryClient();
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: [...PARTNERS_KEY, 'branches', partnerId] });
  const create = useMutation({
    mutationFn: async (body: CreatePartnerBranchRequest): Promise<PartnerBranchResponse> => {
      const response = await api.partners.createBranch({ params: { partnerId }, body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  const update = useMutation({
    mutationFn: async ({
      branchId,
      body,
    }: {
      branchId: string;
      body: UpdatePartnerBranchRequest;
    }): Promise<PartnerBranchResponse> => {
      const response = await api.partners.updateBranch({ params: { partnerId, branchId }, body });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  const setActive = useMutation({
    mutationFn: async ({
      branchId,
      active,
    }: {
      branchId: string;
      active: boolean;
    }): Promise<PartnerBranchResponse> => {
      const response = active
        ? await api.partners.reactivateBranch({ params: { partnerId, branchId }, body: {} })
        : await api.partners.deactivateBranch({ params: { partnerId, branchId }, body: {} });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  return { create, update, setActive };
}

/** Switch off / switch on — MD-01 has no delete path in normal use (MD-RULE-1). */
export function usePartnerActive(partnerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (action: 'deactivate' | 'reactivate'): Promise<PartnerResponse> => {
      const response =
        action === 'deactivate'
          ? await api.partners.deactivate({ params: { partnerId }, body: {} })
          : await api.partners.reactivate({ params: { partnerId }, body: {} });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PARTNERS_KEY }),
  });
}
